import { Injectable } from '@angular/core';
import { type Schema } from '../../../../amplify/data/resource';
import { generateClient } from 'aws-amplify/api';
import { Tenant } from '../models/user.model';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) {
      this._client = generateClient<Schema>();
    }
    return this._client;
  }

  private tenantsSubject = new BehaviorSubject<Tenant[]>([]);
  tenants$ = this.tenantsSubject.asObservable();

  constructor() {
    // Don't call data methods in constructor - they should be called after Amplify is configured
  }

  private _initialized = false;

  async init() {
    if (this._initialized) return;
    this._initialized = true;
    await this.listTenants();
  }

  resetState() {
    this.tenantsSubject.next([]);
    this._initialized = false;
  }

  async listTenants(): Promise<Tenant[]> {
    try {
      const { data, errors } = await this.client.models.Tenant.list({
        filter: { isDeleted: { ne: true } }
      });
      if (errors && errors.length) {
        console.error("Error while getting tenants", errors);
      }
      const tenants = data.map(t => ({
        id: t.id,
        name: t.name,
        isActive: t.isActive ?? true
      })) as Tenant[];
      this.tenantsSubject.next(tenants);
      return tenants;
    } catch (e) {
      console.error("Failed to get all tenants", e);
      return [];
    }
  }

  async createTenant(name: string): Promise<Tenant | null> {
    try {
      const { data, errors } = await this.client.models.Tenant.create({
        name,
        isActive: true
      });
      if (errors && errors.length) {
        console.error("Error creating tenant", errors);
        throw new Error(errors[0].message);
      }
      await this.listTenants();
      return data as unknown as Tenant;
    } catch (e) {
      console.error("Failed to create tenant", e);
      return null;
    }
  }

  async updateTenant(id: string, updates: Partial<Tenant>): Promise<Tenant | null> {
    try {
      const { data, errors } = await this.client.models.Tenant.update({
        id,
        ...updates
      });
      if (errors && errors.length) {
        console.error("Error updating tenant", errors);
        throw new Error(errors[0].message);
      }
      await this.listTenants();
      return data as unknown as Tenant;
    } catch (e) {
      console.error("Failed to update tenant", e);
      return null;
    }
  }

  async deleteTenant(id: string): Promise<boolean> {
    try {
      const { errors } = await this.client.models.Tenant.update({
        id,
        isDeleted: true,
        deletedAt: new Date().toISOString()
      });
      if (errors && errors.length) {
        console.error("Error soft-deleting tenant", errors);
        throw new Error(errors[0].message);
      }
      await this.listTenants();
      return true;
    } catch (e) {
      console.error("Failed to soft-delete tenant", e);
      return false;
    }
  }
}
